/**
 * 화이트보드 URL/Base64 변환 유틸
 * v1 원본: utils/whiteboard/baseUtils.js
 */

/**
 * URL → Base64 DataURL 변환
 * v1의 convertUrlToOnlyBase64File() 포팅
 */
export async function convertUrlToBase64(url: string): Promise<string> {
  const response = await fetch(url, { cache: 'no-cache' });
  const blob = await response.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Base64 DataURL → Blob 변환
 */
export function base64ToBlob(dataURL: string): Blob {
  const [header, base64] = dataURL.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'application/octet-stream';
  const binary = atob(base64);
  const arr = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    arr[i] = binary.charCodeAt(i);
  }
  return new Blob([arr], { type: mime });
}

/**
 * 이미지로부터 Element 데이터 생성 (Excalidraw 이미지 엘리먼트 규격)
 */
export function createImageElementData(
  fileId: string,
  thumbnailURL: string,
  width: number,
  height: number,
  x = 0,
  y = 0,
) {
  return {
    id: fileId,
    type: 'image' as const,
    x,
    y,
    width,
    height,
    fileId,
    status: 'saved' as const,
    // Excalidraw 기본 필드
    angle: 0,
    strokeColor: 'transparent',
    backgroundColor: 'transparent',
    fillStyle: 'hachure' as const,
    strokeWidth: 1,
    strokeStyle: 'solid' as const,
    roughness: 0,
    opacity: 100,
    groupIds: [],
    roundness: null,
    seed: Math.floor(Math.random() * 2 ** 31),
    version: 1,
    versionNonce: Math.floor(Math.random() * 2 ** 31),
    isDeleted: false,
    boundElements: null,
    updated: Date.now(),
    link: null,
    locked: false,
  };
}

// ─── URL 해시 파라미터 유틸 ────────────────────────────────────────────────

const PARAM_BLACKLIST = ['__proto__', 'constructor', 'prototype'];

/**
 * URL 해시 파라미터 파싱.
 * v1: baseUtils.js - parseURLParams()
 *
 * @param url  파싱할 URL 문자열
 * @param dontParse  true면 JSON.parse 하지 않고 raw string 반환
 * @param source  'hash' | 'search'
 */
export function parseURLParams(
  url: string,
  dontParse = false,
  source: 'hash' | 'search' = 'hash',
): Record<string, unknown> {
  if (!url) return {};

  const parsed = new URL(url);
  const paramStr = source === 'search' ? parsed.search : parsed.hash;
  const params: Record<string, unknown> = {};
  const paramParts = paramStr ? paramStr.substring(1).split('&') : [];

  // hash 라우터의 경로 감지 → 무시
  if (source === 'hash' && paramParts.length === 1) {
    const first = paramParts[0];
    if (first.startsWith('/') && !first.includes('&')) return params;
  }

  for (const part of paramParts) {
    const [key, rawValue] = part.split('=');
    if (!key || key.split('.').some((k) => PARAM_BLACKLIST.includes(k))) continue;

    try {
      if (dontParse) {
        params[key] = rawValue;
      } else {
        const decoded = decodeURIComponent(rawValue)
          .replace(/\\&/g, '&')
          .replace(/[\u2018\u2019]/g, "'")
          .replace(/[\u201C\u201D]/g, '"');
        params[key] = decoded === 'undefined' ? undefined : JSON.parse(decoded);
      }
    } catch {
      // 파싱 실패 시 무시
    }
  }

  return params;
}

/**
 * URL 해시에 key=value 파라미터 추가.
 * v1: baseUtils.js - appendURLHashParam()
 */
export function appendURLHashParam(url: string, name: string, value: string): string {
  const newUrl = new URL(url);
  const dummyUrl = new URL('https://example.com');
  dummyUrl.search = newUrl.hash.substring(1);
  dummyUrl.searchParams.append(name, value);
  newUrl.hash = dummyUrl.searchParams.toString();
  return newUrl.toString();
}
